import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { liftangoBrandTokens } from '../../../../theme';

const FONT = '"Roboto", sans-serif';
const TARMAC = liftangoBrandTokens.palette.textPrimary;
const ORANGE = liftangoBrandTokens.palette.primary;
const TEAL = liftangoBrandTokens.palette.secondary;
/** Lines on light chart surface (popover background). */
const AXIS_DIM = 'rgba(58, 58, 72, 0.38)';
const AXIS = 'rgba(58, 58, 72, 0.72)';
/** Column dividers in the stats table (subtle, on light popover). */
const TABLE_COL_DIVIDER = 'rgba(58, 58, 72, 0.14)';

/** Padding fraction on each end of the numeric domain for SVG layout. */
const DOMAIN_PAD_FRAC = 0.04;

/**
 * Five-number summary (+ optional mean) from metrics API — only used when all quartile fields are present.
 */
export interface QuartileDistribution {
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
  mean?: number;
}

function padDomain(min: number, max: number): { d0: number; d1: number } {
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return { d0: min, d1: max };
  }
  if (max === min) {
    const pad = Math.abs(min) * DOMAIN_PAD_FRAC || 0.1;
    return { d0: min - pad, d1: max + pad };
  }
  const span = max - min;
  const pad = span * DOMAIN_PAD_FRAC;
  return { d0: min - pad, d1: max + pad };
}

function xFor(
  v: number,
  d0: number,
  d1: number,
  plotLeft: number,
  plotWidth: number
): number {
  if (!Number.isFinite(v)) return plotLeft;
  if (d1 === d0) return plotLeft + plotWidth / 2;
  return plotLeft + ((v - d0) / (d1 - d0)) * plotWidth;
}

function formatVal(v: number, unit: string): string {
  return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 0 }).format(v)}${unit ? unit : ''}`;
}

export interface MetricsDistributionChartProps {
  title: string;
  unit: string;
  data: QuartileDistribution;
}

/**
 * Horizontal box-and-whisker from metrics API quartiles (wait time, trip distance, etc.).
 */
export const MetricsDistributionChart: React.FC<MetricsDistributionChartProps> = ({ title, unit, data }) => {
  const { t } = useTranslation('simulation');
  const plotLeft = 8;
  const plotWidth = 284;
  const cy = 40;
  const boxHalfH = 14;
  const tickH = 10;

  const { min, q1, median, q3, max, mean } = data;
  const lo = Math.min(min, q1, median, q3, max);
  const hi = Math.max(min, q1, median, q3, max);
  const padded = padDomain(lo, hi);
  const d0 = padded.d0;
  const d1 = padded.d1;
  const x = (v: number) => xFor(v, d0, d1, plotLeft, plotWidth);
  const xMin = x(min);
  const xQ1 = x(q1);
  const xMed = x(median);
  const xQ3 = x(q3);
  const xMax = x(max);

  const hasMean = typeof mean === 'number' && Number.isFinite(mean);
  /** Table shows five-number summary only; mean appears on the chart, not in the table. */
  const headers = [
    t('results.metrics.tableMin'),
    t('results.metrics.tableQ1'),
    t('results.metrics.tableMedian'),
    t('results.metrics.tableQ3'),
    t('results.metrics.tableMax'),
  ];
  const cells = [min, q1, median, q3, max].map((v) => formatVal(v, unit));

  const meanX = hasMean ? x(mean as number) : 0;
  const meanLabelText = hasMean ? t('results.metrics.meanPrefix', { value: formatVal(mean as number, unit) }) : '';
  /** Top of IQR box — mean label (hanging baseline) must sit fully above this with a gap. */
  const boxTopY = cy - boxHalfH;
  /** Pixels above box top reserved for label body (~fontSize 10) + gap so no overlap with box or median. */
  const MEAN_LABEL_CLEARANCE = 16;
  const meanLabelY = boxTopY - MEAN_LABEL_CLEARANCE;
  /** Single-line label height for connector endpoint (~fontSize 10). */
  const MEAN_LABEL_LINE_HEIGHT = 12;
  const meanDotR = 4;
  const meanLabelBottomY = meanLabelY + MEAN_LABEL_LINE_HEIGHT;
  const meanDotTopY = cy - meanDotR;

  return (
    <Box sx={{ minWidth: 300, maxWidth: 360, p: 0 }}>
      <Typography
        sx={{
          fontFamily: FONT,
          fontSize: '0.875rem',
          fontWeight: 700,
          color: TARMAC,
          mb: 1,
        }}
      >
        {title}
      </Typography>
      <Box sx={{ bgcolor: 'rgba(58, 58, 72, 0.06)', borderRadius: 1, p: 1, mb: 1 }}>
        <svg width="100%" height={76} viewBox="0 0 300 76" preserveAspectRatio="xMidYMid meet" role="img" aria-label={title}>
          <title>{title}</title>
          <line x1={xMin} y1={cy} x2={xQ1} y2={cy} stroke={AXIS_DIM} strokeWidth={2} />
          <line x1={xMin} y1={cy - tickH} x2={xMin} y2={cy + tickH} stroke={AXIS} strokeWidth={2} />
          <rect
            x={Math.min(xQ1, xQ3)}
            y={cy - boxHalfH}
            width={Math.abs(xQ3 - xQ1)}
            height={boxHalfH * 2}
            fill={`${TEAL}33`}
            stroke={TEAL}
            strokeWidth={2}
            rx={2}
          />
          <line
            x1={xMed}
            y1={cy - boxHalfH}
            x2={xMed}
            y2={cy + boxHalfH}
            stroke={ORANGE}
            strokeWidth={3}
          />
          <line x1={xQ3} y1={cy} x2={xMax} y2={cy} stroke={AXIS_DIM} strokeWidth={2} />
          <line x1={xMax} y1={cy - tickH} x2={xMax} y2={cy + tickH} stroke={AXIS} strokeWidth={2} />
          {hasMean && (
            <g>
              <line
                x1={meanX}
                y1={meanLabelBottomY}
                x2={meanX}
                y2={meanDotTopY}
                stroke={ORANGE}
                strokeWidth={1}
                opacity={0.72}
                pointerEvents="none"
              />
              <circle cx={meanX} cy={cy} r={meanDotR} fill={ORANGE} stroke="#fff" strokeWidth={1} />
              <text
                x={meanX}
                y={meanLabelY}
                dominantBaseline="hanging"
                textAnchor="middle"
                fill={TARMAC}
                fontSize={10}
                fontFamily={FONT}
                fontWeight={600}
              >
                {meanLabelText}
              </text>
            </g>
          )}
        </svg>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 0.5 }}>
          <LegendItem color={TEAL} label={t('results.metrics.legendIqr')} />
          <LegendItem color={ORANGE} label={t('results.metrics.legendMedian')} />
          {hasMean && <LegendItem color={ORANGE} label={t('results.metrics.legendMean')} variant="meanDot" />}
        </Box>
      </Box>
      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          mt: 0.5,
          '& table': {
            width: '100%',
            borderCollapse: 'collapse',
            fontFamily: FONT,
            fontSize: '0.75rem',
            color: TARMAC,
          },
          '& th': {
            py: 0.5,
            px: 0.5,
            textAlign: 'center',
            fontWeight: 600,
            fontSize: '0.6875rem',
            color: 'text.secondary',
            borderBottom: '1px solid',
            borderColor: 'divider',
            borderLeft: `1px solid ${TABLE_COL_DIVIDER}`,
            whiteSpace: 'nowrap',
            '&:first-of-type': { borderLeft: 'none' },
          },
          '& td': {
            py: 0.65,
            px: 0.5,
            textAlign: 'center',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            borderLeft: `1px solid ${TABLE_COL_DIVIDER}`,
            '&:first-of-type': { borderLeft: 'none' },
          },
        }}
      >
        <table>
          <thead>
            <tr>
              {headers.map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {cells.map((cell, i) => (
                <td key={headers[i]}>{cell}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </Box>
    </Box>
  );
};

const LegendItem: React.FC<{
  color: string;
  label: string;
  /** `meanDot`: single filled circle to match the SVG mean marker (not a multi-segment dash). */
  variant?: 'bar' | 'meanDot';
}> = ({ color, label, variant = 'bar' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    {variant === 'meanDot' ? (
      <Box
        component="span"
        aria-hidden
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: color,
          border: '1px solid #fff',
          boxShadow: `0 0 0 1px ${AXIS_DIM}`,
          flexShrink: 0,
        }}
      />
    ) : (
      <Box
        component="span"
        sx={{
          width: 12,
          height: 3,
          bgcolor: color,
          borderRadius: 0.5,
        }}
      />
    )}
    <Typography component="span" variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
      {label}
    </Typography>
  </Box>
);

/** Returns quartile distribution if all fence values are present and finite. */
export function buildWaitTimeDistribution(sim: {
  waiting_time_min_minimum?: number;
  waiting_time_min_first_quartile?: number;
  waiting_time_min_median?: number;
  waiting_time_min_third_quartile?: number;
  waiting_time_min_maximum?: number;
  waiting_time_min_avg?: number;
}): QuartileDistribution | null {
  const min = sim.waiting_time_min_minimum;
  const q1 = sim.waiting_time_min_first_quartile;
  const median = sim.waiting_time_min_median;
  const q3 = sim.waiting_time_min_third_quartile;
  const max = sim.waiting_time_min_maximum;
  if (
    typeof min !== 'number' ||
    typeof q1 !== 'number' ||
    typeof median !== 'number' ||
    typeof q3 !== 'number' ||
    typeof max !== 'number' ||
    ![min, q1, median, q3, max].every(Number.isFinite)
  ) {
    return null;
  }
  const mean =
    typeof sim.waiting_time_min_avg === 'number' && Number.isFinite(sim.waiting_time_min_avg)
      ? sim.waiting_time_min_avg
      : undefined;
  return { min, q1, median, q3, max, mean };
}

export function buildTripDistanceDistribution(sim: {
  trip_distance_min?: number;
  trip_distance_q1?: number;
  trip_distance_median?: number;
  trip_distance_q3?: number;
  trip_distance_max?: number;
  trip_distance_average?: number;
}): QuartileDistribution | null {
  const min = sim.trip_distance_min;
  const q1 = sim.trip_distance_q1;
  const median = sim.trip_distance_median;
  const q3 = sim.trip_distance_q3;
  const max = sim.trip_distance_max;
  if (
    typeof min !== 'number' ||
    typeof q1 !== 'number' ||
    typeof median !== 'number' ||
    typeof q3 !== 'number' ||
    typeof max !== 'number' ||
    ![min, q1, median, q3, max].every(Number.isFinite)
  ) {
    return null;
  }
  const mean =
    typeof sim.trip_distance_average === 'number' && Number.isFinite(sim.trip_distance_average)
      ? sim.trip_distance_average
      : undefined;
  return { min, q1, median, q3, max, mean };
}
